// ============================================================
//  src/admin/components/AdminUI.js   — shared UI primitives
// ============================================================
import React from 'react';
import './AdminUI.css';

/* ── Stat Card ────────────────────────────────────────────── */
export function AdminStatCard({ icon, label, value, color = 'var(--primary)', sub }) {
  return (
    <div className="aui-stat">
      <div className="aui-stat-icon" style={{ background: color + '18', color }}>
        <i className={`fas ${icon}`} />
      </div>
      <div className="aui-stat-body">
        <p className="aui-stat-label">{label}</p>
        <h3 className="aui-stat-value">{value ?? '—'}</h3>
        {sub && <p className="aui-stat-sub">{sub}</p>}
      </div>
    </div>
  );
}

/* ── Section Header ──────────────────────────────────────── */
export function SectionHeader({ title, subtitle, action }) {
  return (
    <div className="aui-section-hdr">
      <div>
        <h2 className="aui-section-title">{title}</h2>
        {subtitle && <p className="aui-section-sub">{subtitle}</p>}
      </div>
      {action && <div className="aui-section-action">{action}</div>}
    </div>
  );
}

/* ── Card ─────────────────────────────────────────────────── */
export function AdminCard({ children, className = '', style, onClick, onMouseEnter, onMouseLeave }) {
  return (
    <div
      className={`aui-card ${className}`}
      style={style}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      {children}
    </div>
  );
}

/* ── Status Badge ─────────────────────────────────────────── */
export function AdminBadge({ status }) {
  // Numeric DB status codes → string key
  const NUMERIC_MAP = { 0: 'success', 1: 'failed', 2: 'pending', 3: 'reversed', 5: 'processing' };

  const map = {
    success:    { label: 'Success',    cls: 'aui-badge--success' },
    completed:  { label: 'Completed',  cls: 'aui-badge--success' },
    approved:   { label: 'Approved',   cls: 'aui-badge--success' },
    active:     { label: 'Active',     cls: 'aui-badge--success' },
    pending:    { label: 'Pending',    cls: 'aui-badge--warning' },
    processing: { label: 'Processing', cls: 'aui-badge--info'    },
    failed:     { label: 'Failed',     cls: 'aui-badge--danger'  },
    rejected:   { label: 'Rejected',   cls: 'aui-badge--danger'  },
    blocked:    { label: 'Blocked',    cls: 'aui-badge--danger'  },
    inactive:   { label: 'Inactive',   cls: 'aui-badge--muted'   },
    reversed:   { label: 'Reversed',   cls: 'aui-badge--muted'   },
  };

  // Resolve numeric status to string key first, then do string lookup
  const resolved = NUMERIC_MAP[Number(status)] ?? String(status ?? '').toLowerCase();
  const cfg = map[resolved] || { label: String(status ?? '—'), cls: 'aui-badge--muted' };
  return <span className={`aui-badge ${cfg.cls}`}>{cfg.label}</span>;
}

/* ── Button ───────────────────────────────────────────────── */
export function AdminBtn({
  children, variant = 'primary', size = 'md',
  loading, icon, onClick, type = 'button', disabled, style,
}) {
  return (
    <button
      type={type}
      className={`aui-btn aui-btn--${variant} aui-btn--${size}`}
      onClick={onClick}
      disabled={disabled || loading}
      style={style}
    >
      {loading
        ? <span className="aui-btn-spinner" />
        : icon
          ? <i className={`fas ${icon}`} />
          : null}
      {children && <span>{children}</span>}
    </button>
  );
}

/* ── Search Input ─────────────────────────────────────────── */
export function AdminSearch({ value, onChange, placeholder = 'Search…' }) {
  return (
    <div className="aui-search">
      <i className="fas fa-magnifying-glass aui-search-icon" />
      <input
        className="aui-search-input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
      />
      {value && (
        <button className="aui-search-clear" onClick={() => onChange('')}>
          <i className="fas fa-xmark" />
        </button>
      )}
    </div>
  );
}

/* ── Table ────────────────────────────────────────────────── */
export function AdminTable({ cols, rows, onRowClick, emptyText = 'No records found', loading }) {
  if (loading) {
    return (
      <div className="aui-table-loader">
        <span className="spinner spinner-dark" />
      </div>
    );
  }
  return (
    <div className="aui-table-wrap">
      <table className="aui-table">
        <thead>
          <tr>
            {cols.map((c) => (
              <th key={c.key} style={c.style}>{c.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={cols.length} className="aui-table-empty">
                <i className="fas fa-inbox" style={{ fontSize: 28, marginBottom: 8, color: 'var(--text-light)' }} />
                <p>{emptyText}</p>
              </td>
            </tr>
          ) : (
            rows.map((row, i) => (
              <tr
                key={i}
                className={onRowClick ? 'aui-table-row--click' : ''}
                onClick={() => onRowClick?.(row)}
              >
                {cols.map((c) => (
                  <td key={c.key}>
                    {c.render ? c.render(row[c.key], row) : (row[c.key] ?? '—')}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

/* ── Modal ────────────────────────────────────────────────── */
export function AdminModal({ open, onClose, title, children, width = 480, footer, wide }) {
  if (!open) return null;
  const resolvedWidth = wide ? 720 : width;
  return (
    <div className="aui-modal-backdrop" onClick={onClose}>
      <div
        className="aui-modal"
        style={{ maxWidth: resolvedWidth }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="aui-modal-header">
          <h3 className="aui-modal-title">{title}</h3>
          <button className="aui-modal-close" onClick={onClose}>
            <i className="fas fa-xmark" />
          </button>
        </div>
        <div className="aui-modal-body">{children}</div>
        {footer && (
          <div className="aui-modal-footer">
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}

/* ── Form Field ───────────────────────────────────────────── */
export function AdminField({ label, children, hint }) {
  return (
    <div className="aui-field">
      {label && <label className="aui-field-label">{label}</label>}
      {children}
      {hint && <p className="aui-field-hint">{hint}</p>}
    </div>
  );
}

/* ── Input ────────────────────────────────────────────────── */
export function AdminInput({ type = 'text', value, onChange, placeholder, disabled, readOnly }) {
  return (
    <input
      type={type}
      className="aui-input"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      readOnly={readOnly}
    />
  );
}

/* ── Select ───────────────────────────────────────────────── */
export function AdminSelect({ value, onChange, children, disabled }) {
  return (
    <select
      className="aui-input"
      value={value}
      onChange={onChange}
      disabled={disabled}
    >
      {children}
    </select>
  );
}

/* ── Textarea ─────────────────────────────────────────────── */
export function AdminTextarea({ value, onChange, placeholder, rows = 4 }) {
  return (
    <textarea
      className="aui-input aui-textarea"
      value={value}
      onChange={onChange}
      placeholder={placeholder}
      rows={rows}
    />
  );
}

/* ── Page Spinner ─────────────────────────────────────────── */
export function AdminPageLoader() {
  return (
    <div className="aui-page-loader">
      <span className="spinner spinner-dark" style={{ width: 36, height: 36, borderWidth: 3 }} />
    </div>
  );
}

/* ── Empty State ──────────────────────────────────────────── */
export function AdminEmpty({ icon = 'fa-inbox', title, message }) {
  return (
    <div className="aui-empty">
      <i className={`fas ${icon}`} />
      {title && <h4>{title}</h4>}
      {message && <p>{message}</p>}
    </div>
  );
}